import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Modal,
  ModalDialog,
  Stack,
} from "@mui/joy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { UserSimField } from "api/userSimApi";
import {
  AutoFillValues,
  getAutoFillProgress,
  getAutoFillTargetFields,
} from "logic/autoFill";
import {
  formatElapsed,
  formatStepTime,
  getReasoningHeadline,
  parseReasoningLines,
  splitStepReasoning,
} from "logic/autoFillPanel";
import { AutoFillFieldStatus, AutoFillStep } from "hooks/useAutoFillStream";
import useIsInappWebview from "hooks/useIsInappWebview";
import CustomTypography from "components/common/CustomTypography";
import { renderInlineBold } from "components/common/SimpleMdToTypo";
import { sendMessage } from "utils/webview";
import { addAlpha } from "styles/hexOpacity";
import { theme } from "styles/theme";

// 최근 단계만 보여준다 (패널 높이)
const MAX_STEPS = 4;

export const ellipsis = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

// 자동 채우기 진행 패널 — 확인 창이 아니라 진행 상태 표시. 화면 가운데, 너비는 CustomModal 과 같게 (모바일 320px, PC 480px).
// 뒤 화면은 잠근다(빈 공간을 눌러도 닫히지 않음). 다 쓰면 부모가 닫는다.
// 높이가 넘치면 상태·진행률은 두고 그 아래(단계·계획·칸)만 스크롤한다.
export default function AutoFillProgress({
  open,
  isStopping,
  onStop,
  steps,
  fields,
  values,
  fieldStatus,
  rewriteReasons,
  currentKey,
}: {
  open: boolean;
  isStopping: boolean;
  onStop: () => void;
  steps: AutoFillStep[];
  fields: UserSimField[];
  values: AutoFillValues;
  fieldStatus: Record<string, AutoFillFieldStatus>;
  rewriteReasons: Record<string, string>;
  currentKey?: string;
}) {
  const isMobile = useIsInappWebview();
  const progress = getAutoFillProgress(fields, values);
  // 이번에 채울 칸 — 진행률과 같은 기준, 화면 순서
  const targetFields = getAutoFillTargetFields(fields, values);
  const reasoning = steps.map(splitStepReasoning);
  // 이번 계획 — 계획 단계가 끝나도 남긴다
  const plan = [...reasoning].reverse().find((each) => each.plan)?.plan;
  const firstVisibleStep = Math.max(0, steps.length - MAX_STEPS);

  const [isPlanOpen, setIsPlanOpen] = useState(true);
  // 펼친 모델 생각 — 단계 순서(steps index)
  const [openThoughts, setOpenThoughts] = useState<number[]>([]);
  useEffect(() => {
    if (!open) return;
    setIsPlanOpen(true);
    setOpenThoughts([]);
  }, [open]);

  // 경과 시간
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [open]);
  const secondsSince = (time?: number) =>
    time ? Math.max(0, Math.floor((now - time) / 1000)) : 0;

  // 앱(WebView)에 모달 상태 전달 — CustomModal 과 같은 이벤트
  useEffect(() => {
    if (!window.ReactNativeWebView) return;
    const send = (status: "open" | "close") =>
      sendMessage(JSON.stringify({ event: "modal", params: { status } }));
    send(open ? "open" : "close");
    return () => send("close");
  }, [open]);

  return (
    <Modal
      open={open}
      // 빈 공간을 눌러도 닫지 않는다 — 중지 버튼으로만
      onClose={() => {}}
      sx={{
        "& .MuiModal-backdrop": {
          backgroundColor: addAlpha("#000000", 0.5),
        },
      }}
    >
      <ModalDialog
        aria-label="자동 채우기 진행"
        sx={{
          width: isMobile ? "320px" : "480px",
          maxWidth: "calc(100vw - 32px)",
          borderRadius: "24px",
          border: 0,
          boxShadow: theme.shadow.lg,
          backgroundColor: theme.vars.palette.Surface.Forward,
          // CustomModal 과 같은 여백
          px: isMobile ? "20px" : "32px",
          py: isMobile ? "24px" : "32px",
          gap: 0,
        }}
      >
        <Stack spacing={2} sx={{ flexShrink: 0 }}>
          {/* 상태 */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Spinner size="24px" />
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <CustomTypography
                variant={"Body"}
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {isStopping ? "중지하고 있어요" : "자동 채우기 중"}
              </CustomTypography>
            </Stack>
            <Button
              size="sm"
              variant="Plain"
              loading={isStopping}
              onClick={onStop}
              sx={{ flexShrink: 0 }}
            >
              중지
            </Button>
          </Stack>

          {/* 진행률 — 지금 보이는 필수 칸 기준. 첫 값이 오기 전에는 진행률 미정 */}
          <Stack spacing={1}>
            <LinearProgress
              {...(progress.written > 0
                ? {
                    determinate: true,
                    value:
                      progress.total > 0
                        ? (progress.written / progress.total) * 100
                        : 0,
                  }
                : {})}
              // components/onboarding/ProgressBar 와 같은 모양 (진행률 미정 표시가 필요해 Joy LinearProgress 로)
              variant="plain"
              sx={{
                "--LinearProgress-thickness": "8px",
                "--LinearProgress-radius": "100px",
                "--LinearProgress-progressRadius": "100px",
                backgroundColor: theme.vars.palette.Surface.Backward,
                color: theme.vars.palette.Surface.Highlight,
              }}
            />
            {/* 전체 경과 시간 · 칸 수 */}
            <Stack direction="row" justifyContent="space-between">
              <CustomTypography
                variant={"Caption"}
                sx={{ fontWeight: 500, color: theme.vars.palette.Content.Weak }}
              >
                {`경과 ${formatElapsed(secondsSince(steps[0]?.startedAt))}`}
              </CustomTypography>
              {progress.total > 0 && (
                <CustomTypography
                  variant={"Caption"}
                  sx={{
                    fontWeight: 500,
                    color: theme.vars.palette.Content.Weak,
                  }}
                >
                  {`${progress.written} / ${progress.total}칸`}
                </CustomTypography>
              )}
            </Stack>
          </Stack>
        </Stack>

        {/* 단계·계획·칸 — 넘치면 이 안에서만 스크롤 */}
        <Stack
          spacing={2}
          sx={{
            mt: 2,
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
            "& > *": { flexShrink: 0 },
          }}
        >
          {/* 단계 */}
          <Stack spacing={1} sx={sectionSx}>
            {steps.slice(firstVisibleStep).map((step, i) => {
              const index = firstVisibleStep + i;
              const { thoughts } = reasoning[index];
              return (
                <Stack key={`${step.name}_${index}`} spacing={0.75}>
                  <StepRow
                    running={step.status === "running"}
                    time={formatStepTime(step, now)}
                  >
                    {step.label}
                  </StepRow>
                  {/* 단계 결과 요약 (지난 기록 수·읽은 문서·다시 쓰는 칸 수) */}
                  {step.summary && (
                    <CustomTypography
                      variant={"Footnote"}
                      sx={{
                        pl: "24px",
                        fontWeight: 500,
                        color: theme.vars.palette.Content.Weak,
                      }}
                    >
                      {step.summary}
                    </CustomTypography>
                  )}
                  {step.tools.map((tool) => (
                    <Box key={tool.id} sx={{ pl: "24px" }}>
                      <StepRow running={tool.status === "running"} small>
                        {tool.label}
                      </StepRow>
                    </Box>
                  ))}
                  {thoughts.length > 0 && (
                    <Thoughts
                      text={thoughts.join("\n\n")}
                      open={openThoughts.includes(index)}
                      onToggle={() =>
                        setOpenThoughts((prev) =>
                          prev.includes(index)
                            ? prev.filter((each) => each !== index)
                            : [...prev, index]
                        )
                      }
                    />
                  )}
                </Stack>
              );
            })}
          </Stack>

          {/* 이번 계획 (생각기록지) */}
          {plan && (
            <Stack spacing={1} sx={sectionSx}>
              <Toggle
                open={isPlanOpen}
                onClick={() => setIsPlanOpen((prev) => !prev)}
              >
                <CustomTypography
                  variant={"Caption"}
                  sx={{
                    fontWeight: 700,
                    color: theme.vars.palette.Content.Strong,
                  }}
                >
                  이번 계획
                </CustomTypography>
              </Toggle>
              {isPlanOpen && (
                <ReasoningText
                  text={plan}
                  variant="Caption"
                  color={theme.vars.palette.Content.Medium}
                />
              )}
            </Stack>
          )}

          {/* 이번에 채울 칸 */}
          {targetFields.length > 0 && (
            <FieldList
              currentKey={currentKey}
              rows={targetFields.map((field) => ({
                key: field.key,
                title: getFieldTitle(field) ?? "",
                value:
                  values[field.key] !== undefined
                    ? getValueText(field, values[field.key])
                    : undefined,
                // 다시 쓸 칸은 값이 없어도(빈 필수 칸) 다시 씀
                status:
                  fieldStatus[field.key] ??
                  (values[field.key] !== undefined ? "writing" : "waiting"),
                reason:
                  fieldStatus[field.key] === "rewritten"
                    ? rewriteReasons[field.key]
                    : undefined,
              }))}
            />
          )}
        </Stack>
      </ModalDialog>
    </Modal>
  );
}

const sectionSx = {
  p: "12px 16px",
  borderRadius: "12px",
  backgroundColor: theme.vars.palette.Background.Level1,
};

// color: 진행 색 (중지하는 중은 회색)
export function Spinner({ size, color }: { size: string; color?: string }) {
  return (
    <CircularProgress
      sx={{
        flexShrink: 0,
        "--CircularProgress-size": size,
        "--CircularProgress-trackThickness": "2px",
        "--CircularProgress-progressThickness": "2px",
        "--CircularProgress-trackColor": theme.vars.palette.Surface.Backward,
        "--CircularProgress-progressColor":
          color ?? theme.vars.palette.Surface.Highlight,
      }}
    />
  );
}

// 접고 펴는 줄 — 내용 + 오른쪽 화살표
function Toggle({
  open,
  onClick,
  children,
}: {
  open: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-expanded={open}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        width: "100%",
        minWidth: 0,
        p: 0,
        border: "none",
        background: "transparent",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      <KeyboardArrowDownRoundedIcon
        sx={{
          flexShrink: 0,
          fontSize: "18px",
          color: theme.vars.palette.Content.Weak,
          transform: open ? "rotate(180deg)" : "none",
          transition: "transform 0.2s",
        }}
      />
    </Box>
  );
}

// 생각 (Gemini 생각 요약, 영어) — 한 줄로 접어 두고, 누르면 펼친다
export function Thoughts({
  text,
  open,
  onToggle,
}: {
  text: string;
  open: boolean;
  onToggle: () => void;
}) {
  const headline = getReasoningHeadline(text);
  return (
    <Stack spacing={0.5} sx={{ pl: "24px" }}>
      <Toggle open={open} onClick={onToggle}>
        <CustomTypography
          variant={"Footnote"}
          sx={{
            fontWeight: 500,
            color: theme.vars.palette.Content.Weak,
            ...ellipsis,
          }}
        >
          {open || !headline ? "생각" : `생각 · ${headline}`}
        </CustomTypography>
      </Toggle>
      {open && <Reasoning text={text} />}
    </Stack>
  );
}

// 생각 요약 — 흘러나오는 글. 최근 5줄이 보이게 아래로 따라가고, 위로 올려 읽는 중이면 멈춘다
function Reasoning({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const followRef = useRef(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // 새 글로 바뀌어 넘치지 않으면 다시 따라간다
    if (el.scrollHeight <= el.clientHeight) followRef.current = true;
    if (followRef.current) el.scrollTop = el.scrollHeight;
    setScrolled(el.scrollTop > 0);
  }, [text]);

  return (
    <Box
      ref={ref}
      onScroll={(e) => {
        const el = e.currentTarget;
        followRef.current =
          el.scrollHeight - el.scrollTop - el.clientHeight < 8;
        setScrolled(el.scrollTop > 0);
      }}
      sx={{
        // Footnote 줄 높이(12px × 126%) × 5줄
        maxHeight: "76px",
        overflowY: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        // 위로 밀려난 글은 흐리게
        ...(scrolled && {
          maskImage: "linear-gradient(to bottom, transparent, black 16px)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, black 16px)",
        }),
      }}
    >
      <ReasoningText
        text={text}
        variant="Footnote"
        color={theme.vars.palette.Content.Weak}
      />
    </Box>
  );
}

// 생각 요약·계획 글 — 제목·목록·**굵게** 를 해석한다 (흘러오는 중 닫히지 않은 ** 는 숨김)
function ReasoningText({
  text,
  variant,
  color,
}: {
  text: string;
  variant: "Caption" | "Footnote";
  color: string;
}) {
  const sx = { fontWeight: 500, color, wordBreak: "break-word" } as const;
  return (
    <Stack spacing={0.25}>
      {parseReasoningLines(text).map((line, index) =>
        line.type === "item" ? (
          <Stack
            key={index}
            direction="row"
            sx={{ pl: `${line.depth * 14}px` }}
          >
            <CustomTypography
              variant={variant}
              sx={{ ...sx, flexShrink: 0, minWidth: "1.5em" }}
            >
              {line.marker}
            </CustomTypography>
            <CustomTypography variant={variant} sx={{ ...sx, minWidth: 0 }}>
              {renderInlineBold(line.text)}
            </CustomTypography>
          </Stack>
        ) : (
          <CustomTypography
            key={index}
            variant={variant}
            sx={{
              ...sx,
              ...(line.type === "heading" && {
                fontWeight: 700,
                pt: index > 0 ? "6px" : 0,
              }),
            }}
          >
            {renderInlineBold(line.text)}
          </CustomTypography>
        )
      )}
    </Stack>
  );
}

const FIELD_STATUS_LABEL: Record<
  AutoFillFieldStatus | "waiting",
  { label: string; color: string; fontWeight: number }
> = {
  waiting: {
    label: "대기",
    color: theme.vars.palette.Content.SuperWeak,
    fontWeight: 500,
  },
  writing: {
    label: "작성 중",
    color: theme.vars.palette.Content.Medium,
    fontWeight: 500,
  },
  rewritten: {
    label: "다시 씀",
    color: theme.vars.palette.Content.Strong,
    fontWeight: 700,
  },
  confirmed: {
    label: "확정",
    color: theme.vars.palette.Content.Highlight,
    fontWeight: 700,
  },
};

// 이번에 채울 칸 — 칸마다 이름표·값 앞부분·상태. 방금 쓴 칸은 강조하고, 목록 안에서 그 칸이 보이게 따라간다
function FieldList({
  rows,
  currentKey,
}: {
  rows: {
    key: string;
    title: string;
    value?: string;
    status: AutoFillFieldStatus | "waiting";
    // 다시 쓰는 이유 (수정 단계 metadata.fields)
    reason?: string;
  }[];
  currentKey?: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());

  // scrollIntoView 는 패널 스크롤까지 움직이므로 목록 안에서만 스크롤한다
  useEffect(() => {
    const list = listRef.current;
    const row = currentKey ? rowRefs.current.get(currentKey) : undefined;
    if (!list || !row) return;
    const isVisible =
      row.offsetTop >= list.scrollTop &&
      row.offsetTop + row.offsetHeight <= list.scrollTop + list.clientHeight;
    if (!isVisible) {
      list.scrollTo({
        top: row.offsetTop - (list.clientHeight - row.offsetHeight) / 2,
        behavior: "smooth",
      });
    }
  }, [currentKey]);

  return (
    <Box
      ref={listRef}
      sx={{
        ...sectionSx,
        p: "6px",
        position: "relative",
        // 칸 5개 반 정도 — 잘린 칸으로 더 있음을 보인다
        maxHeight: "240px",
        overflowY: "auto",
        overscrollBehavior: "contain",
      }}
    >
      {rows.map((row) => {
        const isCurrent = row.key === currentKey;
        const status = FIELD_STATUS_LABEL[row.status];
        return (
          <Stack
            key={row.key}
            ref={(el: HTMLDivElement | null) => {
              if (el) rowRefs.current.set(row.key, el);
              else rowRefs.current.delete(row.key);
            }}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              p: "6px 10px",
              borderRadius: "8px",
              backgroundColor: isCurrent
                ? theme.vars.palette.Surface.SoftHighlight
                : "transparent",
              transition: "background-color 0.2s",
            }}
          >
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <CustomTypography
                variant={"Caption"}
                sx={{
                  fontWeight: isCurrent ? 700 : 500,
                  color: isCurrent
                    ? theme.vars.palette.Content.Strong
                    : theme.vars.palette.Content.Medium,
                  ...ellipsis,
                }}
              >
                {row.title}
              </CustomTypography>
              {row.value && (
                <CustomTypography
                  variant={"Footnote"}
                  sx={{
                    fontWeight: 500,
                    color: theme.vars.palette.Content.Weak,
                    ...ellipsis,
                  }}
                >
                  {row.value}
                </CustomTypography>
              )}
              {row.reason && (
                <CustomTypography
                  variant={"Footnote"}
                  sx={{
                    fontWeight: 500,
                    color: theme.vars.palette.Content.HighlightWithAlert,
                  }}
                >
                  {row.reason}
                </CustomTypography>
              )}
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.25}
              sx={{ flexShrink: 0 }}
            >
              {row.status === "confirmed" && (
                <CheckRoundedIcon
                  sx={{ fontSize: "14px", color: status.color }}
                />
              )}
              <CustomTypography
                variant={"Footnote"}
                sx={{
                  fontWeight: status.fontWeight,
                  color: status.color,
                  whiteSpace: "nowrap",
                }}
              >
                {status.label}
              </CustomTypography>
            </Stack>
          </Stack>
        );
      })}
    </Box>
  );
}

export function StepRow({
  running,
  small,
  time,
  children,
}: {
  running: boolean;
  small?: boolean;
  time?: string;
  children: React.ReactNode;
}) {
  const isCurrent = running && !small;
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {running ? (
        <Spinner size="16px" />
      ) : (
        <CheckRoundedIcon
          sx={{ fontSize: "16px", color: theme.vars.palette.Content.Highlight }}
        />
      )}
      <CustomTypography
        variant={small ? "Footnote" : "Caption"}
        sx={{
          fontWeight: isCurrent ? 700 : 500,
          color: isCurrent
            ? theme.vars.palette.Content.Strong
            : theme.vars.palette.Content.Medium,
        }}
      >
        {children}
        {time && (
          <Box
            component="span"
            sx={{
              ml: 0.75,
              fontWeight: 500,
              color: theme.vars.palette.Content.Weak,
            }}
          >
            {time}
          </Box>
        )}
      </CustomTypography>
    </Stack>
  );
}

// 칸 이름 — 이름표 → 한글 칸 이름 → 안내 문구 순 (생각기록지 칸 이름은 emotion_domain 같은 영문 id)
export function getFieldTitle(field?: UserSimField) {
  if (!field) return undefined;
  if (field.label) return field.label;
  if (field.name && /[가-힣]/.test(field.name)) return field.name;
  return field.question || field.name || undefined;
}

export function getValueText(
  field: UserSimField | undefined,
  value: AutoFillValues[string]
) {
  if (field?.input === "choice" && typeof value === "number") {
    return field.options[value] ?? `${value}`;
  }
  if (field?.input === "multi_choice" && Array.isArray(value)) {
    return value.map((index) => field.options[index] ?? index).join(", ");
  }
  return `${value}`;
}
